import { Timestamp } from 'firebase/firestore';
import { USERNAME_REGEX } from '@qkindex/contracts';
import {
  changeUsername,
  checkUsernameAvailability,
} from '../firebase/functions';

export const USERNAME_COOLDOWN_DAYS = 20;
export const USERNAME_COOLDOWN_MS =
  USERNAME_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;

export const validateUsernameFormat = (username: string): string | null => {
  const trimmed = username.trim();
  if (trimmed.length < 3) return 'Username must be at least 3 characters long.';
  if (trimmed.length > 20)
    return 'Username must be at most 20 characters long.';
  if (!USERNAME_REGEX.test(trimmed)) {
    return 'Username can only contain letters, numbers, underscores, and hyphens.';
  }
  return null;
};

export const getRemainingCooldownDays = (
  lastChangedAt:
    | Date
    | Timestamp
    | { toMillis?: () => number; getTime?: () => number }
    | null
    | undefined,
  nowMs = Date.now(),
): number => {
  if (!lastChangedAt) return 0;
  let lastMs = 0;
  if (
    typeof (lastChangedAt as { toMillis?: () => number }).toMillis ===
    'function'
  ) {
    lastMs = (lastChangedAt as { toMillis: () => number }).toMillis();
  } else if (
    typeof (lastChangedAt as { getTime?: () => number }).getTime === 'function'
  ) {
    lastMs = (lastChangedAt as { getTime: () => number }).getTime();
  } else if (lastChangedAt instanceof Date) {
    lastMs = lastChangedAt.getTime();
  }
  if (!lastMs) return 0;
  const elapsed = nowMs - lastMs;
  if (elapsed >= USERNAME_COOLDOWN_MS) return 0;
  const remainingMs = USERNAME_COOLDOWN_MS - elapsed;
  return Math.ceil(remainingMs / (24 * 60 * 60 * 1000));
};

export const usernameService = {
  async checkAvailability(
    username: string,
    currentUserId?: string,
  ): Promise<{ available: boolean; message?: string }> {
    const formatError = validateUsernameFormat(username);
    if (formatError) return { available: false, message: formatError };

    void currentUserId;
    try {
      const response = await checkUsernameAvailability({
        username: username.trim(),
      });
      return response.data.available
        ? { available: true }
        : { available: false, message: 'Username is already taken.' };
    } catch (err: unknown) {
      const error = err as { message?: string; code?: string };
      return {
        available: false,
        message:
          error.message ||
          (error.code === 'functions/unauthenticated'
            ? 'Email verification is required.'
            : 'Username availability is temporarily unavailable.'),
      };
    }
  },

  async changeUsername(userId: string, newUsername: string): Promise<string> {
    const trimmed = newUsername.trim();
    const formatError = validateUsernameFormat(trimmed);
    if (formatError) throw new Error(formatError);

    void userId;
    try {
      const response = await changeUsername({ username: trimmed });
      return response.data.username;
    } catch (err: unknown) {
      const error = err as { message?: string; code?: string };
      if (error.message) throw new Error(error.message);
      if (error.code === 'functions/unauthenticated')
        throw new Error('Email verification is required.');
      throw new Error('Username update is temporarily unavailable.');
    }
  },
};
